// 账户状态管理
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { clearUserCache } from '../utils/storage';

interface AccountState {
  address: string | null;
  isConnected: boolean;
  chainId: number | null;
  networkName: string;
  balance: string;
  token: string | null;
  userInfo: {
    address?: string; // Add address to check consistency
    nickname: string;
    level: number;
    isNewUser?: boolean;
    currentHp?: number;
    maxHp?: number;
    exp?: number;
    nextLevelExp?: number;
    myInvitationCode?: string;
  } | null;
}

const initialState: AccountState = {
  address: null,
  isConnected: false,
  chainId: null,
  networkName: '',
  balance: '0',
  token: localStorage.getItem('jwt_token'),
  userInfo: null,
};

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setAccount: (state, action: PayloadAction<{
      address: string;
      isConnected: boolean;
      chainId: number;
    }>) => {
      // If address changed and we have a session, check if it belongs to the same user
      // Note: userInfo might not have address if only login happened (login response doesn't have address)
      // But setUserProfile will set it. 
      // Safest bet: If we have a token, and the new address is different from the one in state (if any),
      // we should probably clear the session to be safe, OR we rely on the API to reject the token if it doesn't match.
      // But for better UX, let's clear if we detect a mismatch.
      
      if (state.address && state.address !== action.payload.address && state.token) {
        console.warn('[Account] Address changed, clearing session');
        state.token = null;
        state.userInfo = null;
        clearUserCache();
      }

      state.address = action.payload.address;
      state.isConnected = action.payload.isConnected;
      state.chainId = action.payload.chainId;
      state.networkName = action.payload.chainId === 56 ? 'BSC主网' : 'BSC测试网';
    },
    setLoginSuccess: (state, action: PayloadAction<{
      token: string;
      expireIn: number;
      userInfo: {
        nickname: string;
        level: number;
        isNewUser?: boolean;
      }
    }>) => {
      state.token = action.payload.token;
      state.userInfo = {
        ...state.userInfo, // Keep existing fields if any (though usually null on login)
        ...action.payload.userInfo
      };
      localStorage.setItem('jwt_token', action.payload.token);
      localStorage.setItem('jwt_expire', (Date.now() + action.payload.expireIn * 1000).toString());
    },
    checkTokenExpiration: (state) => {
      const expire = localStorage.getItem('jwt_expire');
      if (expire && Date.now() > parseInt(expire)) {
        state.token = null;
        state.userInfo = null;
        clearUserCache();
      }
    },
    setUserProfile: (state, action: PayloadAction<{
      address: string;
      nickname: string;
      level: number;
      currentHp: number;
      maxHp: number;
      balance: string;
      exp: number;
      nextLevelExp: number;
      myInvitationCode: string;
    }>) => {
      if (!state.userInfo) {
        state.userInfo = {
          address: action.payload.address,
          nickname: action.payload.nickname,
          level: action.payload.level,
        };
      }
      state.userInfo.address = action.payload.address;
      state.userInfo.nickname = action.payload.nickname;
      state.userInfo.level = action.payload.level;
      state.userInfo.currentHp = action.payload.currentHp;
      state.userInfo.maxHp = action.payload.maxHp;
      state.userInfo.exp = action.payload.exp;
      state.userInfo.nextLevelExp = action.payload.nextLevelExp;
      state.userInfo.myInvitationCode = action.payload.myInvitationCode;
      state.balance = action.payload.balance;
    },
    setBalance: (state, action: PayloadAction<string>) => {
      state.balance = action.payload;
    },
    disconnectWalletState: (state) => {
      // Only clear wallet connection state, keep token and user info
      // This handles page refreshes where wallet temporarily disconnects
      state.address = null;
      state.isConnected = false;
      state.chainId = null;
      state.networkName = '';
    },
    clearAccount: (state) => {
      state.address = null;
      state.isConnected = false;
      state.chainId = null;
      state.networkName = '';
      state.balance = '0';
      state.token = null;
      state.userInfo = null;
      clearUserCache();
    },
  },
});

export const { setAccount, setBalance, clearAccount, setLoginSuccess, setUserProfile, checkTokenExpiration, disconnectWalletState } = accountSlice.actions;

export const selectAccount = (state: RootState) => state.account.address;
export const selectIsConnected = (state: RootState) => state.account.isConnected;
export const selectChainId = (state: RootState) => state.account.chainId;
export const selectNetworkName = (state: RootState) => state.account.networkName;
export const selectBalance = (state: RootState) => state.account.balance;
export const selectToken = (state: RootState) => state.account.token;
export const selectUserInfo = (state: RootState) => state.account.userInfo;

export default accountSlice.reducer;
